import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ArrowLeft, FileText, Upload, Trash2, Download, File, Image, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import * as pdfjsLib from 'pdfjs-dist';

// Set PDF.js worker to use local file from public folder
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const DOCUMENT_CATEGORIES = ['Receipt', 'Manual', 'Warranty', 'Serial Photo', 'License', 'Permit', 'Other'];

// PDF Viewer Component - renders PDF using canvas (no iframe security issues)
function PdfViewer({ fileData }) {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [error, setError] = useState(null);

  // Load PDF document
  useEffect(() => {
    if (!fileData) return;

    const loadPdf = async () => {
      try {
        const base64Data = fileData.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
        setError(null);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF');
      }
    };

    loadPdf();
  }, [fileData]);

  // Render current page
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(currentPage);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;
    } catch (err) {
      console.error('Error rendering page:', err);
    }
  }, [pdfDoc, currentPage, scale]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  if (error) {
    return (
      <div className="h-[500px] flex items-center justify-center text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* Controls */}
      {totalPages > 0 && (
        <div className="flex items-center gap-4 mb-4 p-2 bg-muted rounded-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <div className="border-l border-border pl-4 flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
            >
              -
            </Button>
            <span className="text-sm w-16 text-center">{Math.round(scale * 100)}%</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setScale(s => Math.min(3, s + 0.25))}
            >
              +
            </Button>
          </div>
        </div>
      )}
      
      {/* Canvas */}
      <div className="overflow-auto max-h-[500px] w-full flex justify-center bg-muted/50 rounded-sm">
        <canvas ref={canvasRef} className="shadow-lg" />
      </div>
    </div>
  );
}

export default function DocumentFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const isDetail = isEdit && !window.location.pathname.includes('/edit');

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [document, setDocument] = useState(null);
  const [firearms, setFirearms] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    linked_firearm_id: '',
    linked_accessory_id: '',
    file_data: '',
    file_type: '',
    thumbnail_data: '',
    notes: '',
  });
  const [fileName, setFileName] = useState('');

  useEffect(() => {
    // Fetch firearms and accessories for linking
    api.get('/firearms').then(res => setFirearms(res.data)).catch(() => {});
    api.get('/accessories').then(res => setAccessories(res.data)).catch(() => {});

    if (isEdit) {
      const fetchDocument = async () => {
        try {
          const response = await api.get(`/documents/${id}`);
          setDocument(response.data);
          setFormData({
            name: response.data.name || '',
            category: response.data.category || '',
            linked_firearm_id: response.data.linked_firearm_id || '',
            linked_accessory_id: response.data.linked_accessory_id || '',
            file_data: response.data.file_data || '',
            file_type: response.data.file_type || '',
            thumbnail_data: response.data.thumbnail_data || '',
            notes: response.data.notes || '',
          });
        } catch (error) {
          toast.error('Document not found');
          navigate('/documents');
        } finally {
          setLoading(false);
        }
      };
      fetchDocument();
    }
  }, [id, isEdit, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    if (value === 'none') value = '';
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Generate thumbnail for image files
  const generateImageThumbnail = (dataUrl) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = window.document.createElement('canvas');
        const maxWidth = 320;
        const scale = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => resolve('');
      img.src = dataUrl;
    });
  };

  // Generate thumbnail for PDF files (first page)
  const generatePdfThumbnail = async (dataUrl) => {
    try {
      // Extract base64 data from data URL
      const base64Data = dataUrl.split(',')[1];
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
      const page = await pdf.getPage(1);
      
      const viewport = page.getViewport({ scale: 1 });
      const maxWidth = 320;
      const scale = maxWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });
      
      const canvas = window.document.createElement('canvas');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      const ctx = canvas.getContext('2d');
      
      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      return canvas.toDataURL('image/jpeg', 0.8);
    } catch (error) {
      console.error('PDF thumbnail generation failed:', error);
      return '';
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const fileDataUrl = reader.result;
      let thumbnailData = '';
      
      // Generate thumbnail based on file type
      if (file.type.startsWith('image/')) {
        thumbnailData = await generateImageThumbnail(fileDataUrl);
      } else if (file.type === 'application/pdf') {
        thumbnailData = await generatePdfThumbnail(fileDataUrl);
      }
      
      setFormData(prev => ({
        ...prev,
        file_data: fileDataUrl,
        file_type: file.type,
        thumbnail_data: thumbnailData,
        name: prev.name || file.name.replace(/\.[^/.]+$/, ''),
      }));
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category) {
      toast.error('Please fill in required fields');
      return;
    }

    if (!isEdit && !formData.file_data) {
      toast.error('Please upload a file');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/documents/${id}`, {
          name: formData.name,
          category: formData.category,
          linked_firearm_id: formData.linked_firearm_id || null,
          linked_accessory_id: formData.linked_accessory_id || null,
          notes: formData.notes,
        });
        toast.success('Document updated');
      } else {
        await api.post('/documents', formData);
        toast.success('Document uploaded');
      }
      navigate('/documents');
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/documents/${id}`);
      toast.success('Document deleted');
      navigate('/documents');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = () => {
    if (!document?.file_data) return;
    
    const link = window.document.createElement('a');
    link.href = document.file_data;
    link.download = document.name;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="w-12 h-12 text-destructive" />;
    if (fileType?.includes('image')) return <Image className="w-12 h-12 text-secondary" />;
    return <File className="w-12 h-12 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <Layout title={isEdit ? 'Document Details' : 'Add Document'}>
        <div className="animate-pulse space-y-6 max-w-2xl">
          <div className="h-48 bg-muted rounded" />
          <div className="h-10 bg-muted rounded w-full" />
        </div>
      </Layout>
    );
  }

  // Detail view
  if (isDetail && document) {
    const linkedFirearm = firearms.find(f => f.id === document.linked_firearm_id);
    const linkedAccessory = accessories.find(a => a.id === document.linked_accessory_id);

    return (
      <Layout title="Document Details">
        <div className="max-w-3xl" data-testid="document-detail-page">
          <Button
            variant="ghost"
            onClick={() => navigate('/documents')}
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Documents
          </Button>

          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground font-['Chivo'] tracking-tight">
                  {document.name}
                </h1>
                <p className="text-muted-foreground">{document.category}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="rounded-sm"
                  data-testid="download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/documents/${id}/edit`)}
                  className="rounded-sm"
                  data-testid="edit-btn"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      data-testid="delete-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Document</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this document?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        disabled={deleting}
                      >
                        {deleting ? 'Deleting...' : 'Delete'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-card border border-border rounded-sm p-4">
              {document.file_type?.includes('image') ? (
                <img
                  src={document.file_data}
                  alt={document.name}
                  className="max-w-full max-h-[500px] mx-auto object-contain"
                />
              ) : document.file_type?.includes('pdf') ? (
                <PdfViewer fileData={document.file_data} />
              ) : (
                <div className="h-48 flex flex-col items-center justify-center">
                  {getFileIcon(document.file_type)}
                  <p className="text-muted-foreground mt-4">Preview not available</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    className="rounded-sm mt-2"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download to view
                  </Button>
                </div>
              )}
            </div>

            {/* Linked Items */}
            {(linkedFirearm || linkedAccessory) && (
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                  Linked Items
                </h3>
                {linkedFirearm && (
                  <div
                    className="flex items-center gap-3 p-2 hover:bg-muted rounded-sm cursor-pointer transition-colors"
                    onClick={() => navigate(`/firearms/${linkedFirearm.id}`)}
                  >
                    <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center">
                      <FileText className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="font-medium">{linkedFirearm.manufacturer} {linkedFirearm.model}</p>
                      <p className="text-xs text-muted-foreground">Firearm</p>
                    </div>
                  </div>
                )}
                {linkedAccessory && (
                  <div
                    className="flex items-center gap-3 p-2 hover:bg-muted rounded-sm cursor-pointer transition-colors"
                    onClick={() => navigate(`/accessories/${linkedAccessory.id}`)}
                  >
                    <div className="w-10 h-10 bg-muted rounded-sm flex items-center justify-center">
                      <FileText className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="font-medium">{linkedAccessory.name}</p>
                      <p className="text-xs text-muted-foreground">Accessory</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {document.notes && (
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                  Notes
                </h3>
                <p className="text-foreground whitespace-pre-wrap">{document.notes}</p>
              </div>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // Form view
  return (
    <Layout title={isEdit ? 'Edit Document' : 'Add Document'}>
      <div className="max-w-2xl" data-testid="document-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/documents')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Documents
        </Button>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          {!isEdit && (
            <div className="bg-card border border-border rounded-sm p-6">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                Upload File
              </h3>
              
              {formData.file_data ? (
                <div className="flex items-center gap-4 p-4 bg-muted rounded-sm">
                  {getFileIcon(formData.file_type)}
                  <div className="flex-1">
                    <p className="font-medium">{fileName}</p>
                    <p className="text-sm text-muted-foreground">{formData.file_type}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, file_data: '', file_type: '' }));
                      setFileName('');
                    }}
                    className="rounded-sm"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <label className="border-2 border-dashed border-border rounded-sm p-8 flex flex-col items-center justify-center cursor-pointer hover:border-secondary transition-colors">
                  <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                  <span className="text-muted-foreground">Click to upload PDF or image</span>
                  <span className="text-xs text-muted-foreground mt-1">PDF, JPEG, PNG supported</span>
                  <input
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/webp"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="input-file"
                  />
                </label>
              )}
            </div>
          )}

          {/* Document Info */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
              Document Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Glock 19 Receipt"
                  className="rounded-sm"
                  required
                  data-testid="input-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => handleSelectChange('category', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link to Firearm</Label>
                <Select value={formData.linked_firearm_id || 'none'} onValueChange={(v) => handleSelectChange('linked_firearm_id', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-firearm">
                    <SelectValue placeholder="Select firearm" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {firearms.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.manufacturer} {f.model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Link to Accessory</Label>
                <Select value={formData.linked_accessory_id || 'none'} onValueChange={(v) => handleSelectChange('linked_accessory_id', v)}>
                  <SelectTrigger className="rounded-sm" data-testid="select-accessory">
                    <SelectValue placeholder="Select accessory" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {accessories.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Add any notes..."
                rows={3}
                className="rounded-sm"
                data-testid="input-notes"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-muted hover:bg-muted/80 text-foreground rounded-sm btn-tactical"
              data-testid="submit-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Upload Document'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/documents')}
              className="rounded-sm"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
